import React, { useState, useEffect } from 'react';
import { api } from '../api';

interface UserProfile {
  id: number;
  email: string;
  role: string;
  first_name: string;
  last_name: string;
  reimbursement_currency: string;
}

const Settings = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserProfile(response.data);
      console.log('User profile loaded:', response.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      setMessageType('error');
      setMessage('Failed to load profile information');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (!token) {
      setMessageType('error');
      setMessage('No token found. Please log in again.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    navigator.clipboard.writeText(token)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(() => {
        setMessageType('error');
        setMessage('Unable to copy token. Please try again.');
        setTimeout(() => setMessage(''), 3000);
      });
  };

  // Role display mapping
  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'partner': return 'Partner';
      case 'admin': return 'Partner'; // Legacy mapping
      case 'team_member': return 'Team Member';
      case 'user': return 'Team Member'; // Legacy mapping
      default: return role;
    }
  };

  const handlePasswordReset = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessageType('error');
      setMessage('New passwords do not match');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setMessageType('error');
      setMessage('New password must be at least 6 characters');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await api.put('/api/auth/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setMessageType('success');
      setMessage('Password updated successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessageType('error');
      if (error.response?.status === 400) {
        setMessage(error.response.data.message || 'Invalid current password');
      } else {
        setMessage('Failed to update password');
      }
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #e5e7eb', 
            borderTop: '3px solid #3b82f6', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite' 
          }}></div>
          <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading your settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <div style={{ padding: '16px 16px 0 16px', marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
          Settings
        </h1>
        <p style={{ margin: 0, fontSize: '16px', color: '#6b7280' }}>
          Manage your profile information and account preferences
        </p>
      </div>

      <div style={{ padding: '16px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Profile Information - Read Only */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '24px', 
          borderRadius: '12px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
          border: '1px solid #e5e7eb',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#dbeafe',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              👤
            </div>
            <div>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                Profile Information
              </h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                Your account details (managed by administrators)
              </p>
            </div>
          </div>
          
          <div style={{ display: 'grid', gap: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  First Name
                </label>
                <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                  {userProfile?.first_name || 'Not set'}
                </div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Last Name
                </label>
                <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                  {userProfile?.last_name || 'Not set'}
                </div>
              </div>
            </div>
            
            <div style={{
              padding: '16px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Email Address
              </label>
              <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                {userProfile?.email}
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Role
                </label>
                <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                  {getRoleDisplayName(userProfile?.role || '')}
                </div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Reimbursement Currency
                </label>
                <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                  {userProfile?.reimbursement_currency || 'EUR'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* iOS Shortcut Token */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#fef3c7',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              🔑
            </div>
            <div>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                iOS Shortcut Token
              </h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                Copy your current session token to paste into the iOS shortcut (valid ~24h after login).
              </p>
            </div>
          </div>

          <button
            onClick={handleCopyToken}
            style={{
              backgroundColor: copySuccess ? '#10b981' : '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            {copySuccess ? '✅ Copied!' : '📋 Copy iOS Token'}
          </button>

          <div style={{
            marginTop: '16px',
            background: '#fff7ed',
            border: '1px solid #fdba74',
            borderRadius: '8px',
            padding: '12px',
            fontSize: '13px',
            color: '#9a3412'
          }}>
            ⚠️ Token hidden for security. Click "Copy iOS Token" to place it on your clipboard, keep it private, and if the shortcut shows "token expired" just sign out/in and copy a fresh token.
          </div>
        </div>

        {/* Password Reset Section */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          marginTop: '24px'
        }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
            🔒 Change Password
          </h2>
          
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Current Password
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={e => setPasswordData({...passwordData, currentPassword: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                placeholder="Enter current password"
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                New Password
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={e => setPasswordData({...passwordData, newPassword: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={e => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                placeholder="Confirm new password"
              />
            </div>
            
            <button
              onClick={handlePasswordReset}
              disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
              style={{
                backgroundColor: passwordData.currentPassword && passwordData.newPassword && passwordData.confirmPassword ? '#10b981' : '#9ca3af',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: passwordData.currentPassword && passwordData.newPassword && passwordData.confirmPassword ? 'pointer' : 'not-allowed',
                width: 'fit-content'
              }}
            >
              Update Password
            </button>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: messageType === 'success' ? '#10b981' : '#ef4444',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>{messageType === 'success' ? '✅' : '❌'}</span>
            {message}
            <button
              onClick={() => setMessage('')}
              style={{ 
                marginLeft: '8px', 
                background: 'none', 
                border: 'none', 
                color: 'white', 
                cursor: 'pointer',
                fontSize: '16px',
                padding: '0',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
